import { createFileRoute, redirect } from '@tanstack/react-router'
import { useEffect, useMemo, useState } from 'react'
import engagementRadarArt from '@/assets/landing/engagement-radar.svg'
import outreachPlaybookArt from '@/assets/landing/outreach-playbook.svg'
import warmLeadQueueArt from '@/assets/landing/warm-lead-queue.svg'
import {
  api,
  type AudienceWorkspaceSnapshot,
  type DashboardLead,
  type DashboardLeadStatus,
} from '@/lib/api'
import { isAuthenticated } from '@/lib/auth-store'
import styles from './audience.module.css'

export const Route = createFileRoute('/audience')({
  beforeLoad: () => {
    if (!isAuthenticated()) {
      throw redirect({ to: '/login' })
    }
  },
  component: AudienceRoute,
})

type LeadFilter = 'all' | DashboardLeadStatus

const FILTERS: Array<{ id: LeadFilter; label: string }> = [
  { id: 'all', label: 'All' },
  { id: 'new', label: 'New' },
  { id: 'reviewing', label: 'Reviewing' },
  { id: 'contacted', label: 'Contacted' },
]

const LEAD_VISUALS = [engagementRadarArt, warmLeadQueueArt, outreachPlaybookArt]

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)

  if (parts.length === 0) {
    return 'SB'
  }

  if (parts.length === 1) {
    return parts[0]!.slice(0, 2).toUpperCase()
  }

  return `${parts[0]![0] ?? ''}${parts[1]![0] ?? ''}`.toUpperCase()
}

function getIntentClassName(intent: 'High' | 'Medium' | 'Low'): string {
  if (intent === 'High') {
    return styles.intentHigh ?? ''
  }

  if (intent === 'Medium') {
    return styles.intentMedium ?? ''
  }

  return styles.intentLow ?? ''
}

function AudienceRoute() {
  const [workspace, setWorkspace] = useState<AudienceWorkspaceSnapshot | null>(null)
  const [selectedLeadId, setSelectedLeadId] = useState('')
  const [activeFilter, setActiveFilter] = useState<LeadFilter>('all')
  const [notice, setNotice] = useState<string | null>(null)
  const [savingLeadId, setSavingLeadId] = useState<string | null>(null)

  useEffect(() => {
    const loadAudience = async (): Promise<void> => {
      try {
        const data = await api.getAudienceWorkspace()
        setWorkspace(data)
        setSelectedLeadId((current) => current || data.leads[0]?.id || '')
      } catch (error) {
        setNotice(error instanceof Error ? error.message : 'Failed to load audience workspace.')
      }
    }

    void loadAudience()
  }, [])

  const visibleLeads = useMemo(() => {
    const leads = workspace?.leads ?? []
    if (activeFilter === 'all') {
      return leads
    }

    return leads.filter((lead) => lead.status === activeFilter)
  }, [activeFilter, workspace?.leads])

  const selectedLead = useMemo(() => {
    if (!workspace) {
      return null
    }

    return (
      workspace.leads.find((lead) => lead.id === selectedLeadId) ??
      visibleLeads[0] ??
      workspace.leads[0] ??
      null
    )
  }, [selectedLeadId, visibleLeads, workspace])

  const counts = workspace?.counts ?? {
    new: 0,
    reviewing: 0,
    contacted: 0,
    highIntent: 0,
    total: 0,
  }

  const selectedLeadVisual =
    selectedLead != null
      ? LEAD_VISUALS[
          Math.abs(selectedLead.id.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0)) %
            LEAD_VISUALS.length
        ]
      : engagementRadarArt

  const updateLeadInState = (
    leadId: string,
    updater: (lead: DashboardLead) => DashboardLead
  ): void => {
    setWorkspace((current) => {
      if (!current) {
        return current
      }

      return {
        ...current,
        leads: current.leads.map((lead) => (lead.id === leadId ? updater(lead) : lead)),
      }
    })
  }

  const saveLeadPatch = async (
    leadId: string,
    payload: {
      status?: DashboardLeadStatus
      outreachMessage?: string
    },
    successNotice?: string
  ): Promise<void> => {
    setSavingLeadId(leadId)
    try {
      const currentLead = workspace?.leads.find((lead) => lead.id === leadId)
      const response = await api.updateAudienceLead(leadId, {
        ...payload,
        ...(currentLead?.lastUpdatedAt ? { expectedLastUpdatedAt: currentLead.lastUpdatedAt } : {}),
      })
      setWorkspace(response.workspace)
      if (successNotice) {
        setNotice(successNotice)
      }
    } catch (error) {
      setNotice(error instanceof Error ? error.message : 'Unable to update lead right now.')
    } finally {
      setSavingLeadId(null)
    }
  }

  const handleCopyMessage = async (): Promise<void> => {
    if (!selectedLead) {
      return
    }

    try {
      await navigator.clipboard.writeText(selectedLead.outreachMessage)
      setNotice(`Outreach message copied for ${selectedLead.name}.`)
    } catch {
      setNotice('Clipboard unavailable. Select and copy the message manually.')
    }
  }

  return (
    <section className={styles.page}>
      <header className={styles.hero}>
        <div className={styles.heroCopy}>
          <p className={styles.overline}>Audience</p>
          <h1 className={styles.title}>Warm responders and outreach actions</h1>
          <p className={styles.subtitle}>
            Prioritize who to contact, see the content driving their response, and move leads from
            signal to conversation.
          </p>
          <p className={styles.subtitle}>
            Images are safe on LinkedIn and can increase engagement when they stay relevant to the
            message.
          </p>
        </div>
        <figure className={styles.heroVisual}>
          <img src={engagementRadarArt} alt="Audience engagement radar visual" />
        </figure>
      </header>

      {notice && <p className={styles.notice}>{notice}</p>}

      <section className={styles.statGrid}>
        <article className={styles.statCard}>
          <p className={styles.statLabel}>New</p>
          <p className={styles.statValue}>{counts.new}</p>
          <p className={styles.statHint}>Need first action</p>
        </article>
        <article className={styles.statCard}>
          <p className={styles.statLabel}>Reviewing</p>
          <p className={styles.statValue}>{counts.reviewing}</p>
          <p className={styles.statHint}>Message in progress</p>
        </article>
        <article className={styles.statCard}>
          <p className={styles.statLabel}>Contacted</p>
          <p className={styles.statValue}>{counts.contacted}</p>
          <p className={styles.statHint}>Follow-up queued</p>
        </article>
        <article className={styles.statCard}>
          <p className={styles.statLabel}>High intent</p>
          <p className={styles.statValue}>{counts.highIntent}</p>
          <p className={styles.statHint}>Start here first</p>
        </article>
        <article className={styles.statCard}>
          <p className={styles.statLabel}>Total leads</p>
          <p className={styles.statValue}>{counts.total}</p>
          <p className={styles.statHint}>Active queue size</p>
        </article>
      </section>

      <section className={styles.panel}>
        <div className={styles.panelHeader}>
          <div>
            <p className={styles.panelEyebrow}>Responder queue</p>
            <h2 className={styles.panelTitle}>Who should you contact next</h2>
          </div>
          <p className={styles.muted}>Filter by workflow stage for a focused outreach session</p>
        </div>

        <div className={styles.filterRow}>
          {FILTERS.map((filter) => (
            <button
              key={filter.id}
              type="button"
              className={activeFilter === filter.id ? styles.filterPillActive : styles.filterPill}
              onClick={() => setActiveFilter(filter.id)}
            >
              {filter.label}
            </button>
          ))}
        </div>

        <div className={styles.workspaceGrid}>
          <div className={styles.leadList}>
            {visibleLeads.map((lead, index) => {
              const cardVisual = LEAD_VISUALS[index % LEAD_VISUALS.length]
              const isSelected = selectedLead?.id === lead.id
              return (
                <article
                  key={lead.id}
                  className={`${styles.leadCard} ${isSelected ? styles.leadCardSelected : ''}`}
                >
                  <button
                    type="button"
                    className={styles.leadButton}
                    onClick={() => setSelectedLeadId(lead.id)}
                  >
                    <span className={styles.avatar}>{getInitials(lead.name)}</span>

                    <span className={styles.leadBody}>
                      <span className={styles.leadNameRow}>
                        <span className={styles.leadName}>{lead.name}</span>
                        <span className={getIntentClassName(lead.intent)}>{lead.intent}</span>
                      </span>
                      <span className={styles.leadMeta}>
                        {lead.headline} at {lead.company}
                      </span>
                      <span className={styles.leadMeta}>
                        {lead.signalSummary} • {lead.lastEngagedAt} • {lead.status}
                      </span>
                    </span>

                    <img src={cardVisual} alt="" className={styles.leadVisual} />
                  </button>
                </article>
              )
            })}

            {visibleLeads.length === 0 && (
              <p className={styles.empty}>No leads in this segment yet.</p>
            )}
          </div>

          {selectedLead && (
            <aside className={styles.detailPanel}>
              <div className={styles.detailTop}>
                <div className={styles.detailIdentity}>
                  <span className={styles.avatar}>{getInitials(selectedLead.name)}</span>
                  <div>
                    <h3 className={styles.detailHeading}>{selectedLead.name}</h3>
                    <p className={styles.detailSub}>
                      {selectedLead.headline} at {selectedLead.company}
                    </p>
                  </div>
                </div>

                <div className={styles.detailHeroImage}>
                  <img src={selectedLeadVisual} alt="" />
                </div>
              </div>

              <div className={styles.factGrid}>
                <article className={styles.factCard}>
                  <p className={styles.factLabel}>Recent signal</p>
                  <p className={styles.factValue}>{selectedLead.signalSummary}</p>
                </article>
                <article className={styles.factCard}>
                  <p className={styles.factLabel}>Status</p>
                  <p className={styles.factValue}>{selectedLead.status}</p>
                </article>
                <article className={styles.factCard}>
                  <p className={styles.factLabel}>Intent</p>
                  <p className={styles.factValue}>{selectedLead.intent}</p>
                </article>
                <article className={styles.factCard}>
                  <p className={styles.factLabel}>Last engagement</p>
                  <p className={styles.factValue}>{selectedLead.lastEngagedAt}</p>
                </article>
              </div>

              <article className={styles.postImpactCard}>
                <p className={styles.postLabel}>Content driving response</p>
                <h4 className={styles.postTitle}>{selectedLead.recentPostTitle}</h4>
                <p className={styles.postOutcome}>{selectedLead.recentPostOutcome}</p>
              </article>

              <p className={styles.muted}>
                <strong>Recommended action:</strong> {selectedLead.recommendedAction}
              </p>

              <label className={styles.inputLabel}>
                Outreach draft
                <textarea
                  value={selectedLead.outreachMessage}
                  onChange={(event) => {
                    const outreachMessage = event.target.value
                    updateLeadInState(selectedLead.id, (lead) => ({
                      ...lead,
                      outreachMessage,
                    }))
                  }}
                  onBlur={(event) => {
                    void saveLeadPatch(
                      selectedLead.id,
                      { outreachMessage: event.target.value },
                      `${selectedLead.name}'s outreach draft saved.`
                    )
                  }}
                />
              </label>

              <div className={styles.actionRow}>
                <button
                  type="button"
                  className={styles.actionButton}
                  disabled={savingLeadId === selectedLead.id}
                  onClick={() => {
                    void saveLeadPatch(
                      selectedLead.id,
                      { status: 'reviewing' },
                      `${selectedLead.name} moved to reviewing.`
                    )
                  }}
                >
                  Mark reviewing
                </button>
                <button
                  type="button"
                  className={styles.actionButton}
                  disabled={savingLeadId === selectedLead.id}
                  onClick={() => {
                    void saveLeadPatch(
                      selectedLead.id,
                      { status: 'contacted' },
                      `${selectedLead.name} marked as contacted.`
                    )
                  }}
                >
                  Mark contacted
                </button>
                <button
                  type="button"
                  className={`${styles.actionButton} ${styles.actionButtonSecondary}`}
                  onClick={() => void handleCopyMessage()}
                >
                  Copy message
                </button>
              </div>
            </aside>
          )}
        </div>
      </section>
    </section>
  )
}
